import { ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { getAllBlogPosts } from '../data/blogPosts';

interface RelatedArticlesProps {
  currentSlug?: string;
  title?: string;
  showCount?: number;
}

export default function RelatedArticles({ currentSlug, title = "Related Articles", showCount = 2 }: RelatedArticlesProps) {
  // Get all blog posts, optionally excluding the current one
  const allPosts = getAllBlogPosts().filter(post => currentSlug ? post.slug !== currentSlug : true);
  
  // Create a deterministic shuffle using the current slug as seed
  const seededShuffle = (array: typeof allPosts, seed: string) => {
    // Create a better hash from the seed
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Simple seeded random number generator (mulberry32)
    const seededRandom = (function() {
      let a = hash;
      return function() {
        a |= 0; a = a + 0x6D2B79F5 | 0;
        let t = Math.imul(a ^ a >>> 15, 1 | a);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
      };
    })();
    
    // Fisher-Yates shuffle with seeded random
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  // If no currentSlug provided (like on homepage), just show the latest posts
  const relatedPosts = currentSlug 
    ? seededShuffle(allPosts, currentSlug).slice(0, showCount)
    : allPosts.slice(0, showCount);

  // Determine grid columns based on number of posts
  const getGridClasses = () => {
    if (relatedPosts.length === 1) return "grid grid-cols-1 gap-8 max-w-md mx-auto";
    if (relatedPosts.length === 2) return "grid md:grid-cols-2 gap-8";
    return "grid md:grid-cols-2 lg:grid-cols-3 gap-8";
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">{title}</h3>
      <div className={getGridClasses()}>
        {relatedPosts.map((post) => (
          <Link
            key={post.id}
            href={`/blog/${post.slug}`}
            className="block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
          >
            <article>
              <img
                src={post.featuredImage}
                alt={post.featuredImageAltText}
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {post.category}
                  </span>
                  <div className="flex items-center text-xs text-gray-500">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {post.readTime}
                  </div>
                </div>
                
                <h4 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                  {post.title}
                </h4>
                
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {new Date(post.publishDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  
                  <div className="inline-flex items-center text-blue-600 group-hover:text-blue-700 font-medium text-sm">
                    Read More
                    <ArrowRightIcon className="h-3 w-3 ml-1" />
                  </div>
                </div>
              </div>
            </article>
          </Link>
        ))}
      </div>
    </div>
  );
}
